import {
  Component, ChangeDetectionStrategy,
  OnInit, OnDestroy, inject, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { NzTableModule } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';

import { FikoYTKokBilesenComponent } from '@service/kok-bilesen/kok-bilesen.component';
import { FirmaService } from '@uygulamalar/firma-yonetim/services/firma.service';
import type { IFirmaPersonel, IFirmaDepartman } from '@uygulamalar/firma-yonetim/interfaces';
import { hmiAyarGetir } from '@uygulamalar/hmi-pano/config/hmi-parametre-setleri';
import { KartWebSocketService } from '@uygulamalar/hmi-pano/kart-okuyucu/services/kart-websocket.service';

@Component({
  selector: 'hmi-pano-personel',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterModule,
    NzTableModule, NzButtonModule, NzIconModule, NzInputModule,
    NzTagModule, NzBadgeModule, NzTooltipModule, NzEmptyModule,
    NzSpinModule, NzModalModule, NzFormModule, NzSelectModule,
    NzAlertModule, NzDescriptionsModule, NzDividerModule,
    NzPopconfirmModule, NzDatePickerModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './personel.component.html',
  styleUrl: './personel.component.less',
})
export class HmiPanoPersonelComponent extends FikoYTKokBilesenComponent implements OnInit, OnDestroy {
  private firmaService = inject(FirmaService);
  private wsService = inject(KartWebSocketService);
  private destroy$ = new Subject<void>();

  constructor() { super(); }

  firmaId = '';

  // Tablo
  yukleniyor = signal(false);
  liste = signal<IFirmaPersonel[]>([]);
  toplam = signal(0);
  mevcutSayfa = 1;
  sayfaLimiti = 20;
  aramaMetni = '';
  seciliDepartmanId: string | null = null;
  departmanlar = signal<IFirmaDepartman[]>([]);
  istatistikler = signal({ toplam: 0, aktif: 0, kartli: 0 });

  // Form
  formGorunur = false;
  formDuzenlemeModu = false;
  formKaydediliyor = signal(false);
  private formPersonelId = '';
  form = this.bosForm();

  // Kart modal
  kartModalGorunur = false;
  kartKaydediliyor = signal(false);
  kartHedefPersonel: IFirmaPersonel | null = null;
  kartUidGirdi = '';
  kartDinleniyor = signal(false);

  // Detay
  detayGorunur = false;
  detayPersonel: IFirmaPersonel | null = null;

  private aramaTimer: any;

  private readonly renkler = [
    '#1677ff', '#52c41a', '#722ed1', '#eb2f96', '#fa8c16',
    '#13c2c2', '#2f54eb', '#f5222d', '#a0d911', '#fadb14',
  ];

  ngOnInit() {
    this.firmaId = hmiAyarGetir<string>('firmaId', '');

    if (this.firmaId) {
      this.departmanlariYukle();
      this.yukle();
      this.istatistikleriYukle();
    }

    // Kart okuyucudan gelen UID'leri kart atama modalına yönlendir
    this.wsService.veriGeldi$.pipe(takeUntil(this.destroy$)).subscribe(veri => {
      if (this.kartModalGorunur && this.kartDinleniyor()) {
        const uid = this.uidCikar(veri);
        if (uid) {
          this.kartUidGirdi = uid;
          this.kartDinleniyor.set(false);
          this.CD.markForCheck();
        }
      }
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
    if (this.aramaTimer) clearTimeout(this.aramaTimer);
  }

  geriDon() {
    this.router.navigate(['..'], { relativeTo: this.route });
  }

  // ─── VERİ ─────────────────────────────────────────

  async yukle() {
    if (!this.firmaId) return;
    this.yukleniyor.set(true);
    try {
      const sonuc = await this.firmaService.personelListesi(
        this.firmaId, this.mevcutSayfa, this.sayfaLimiti,
        {
          arama: this.aramaMetni || undefined,
          departmanId: this.seciliDepartmanId || undefined,
        },
      );
      this.liste.set(sonuc.liste || []);
      this.toplam.set(sonuc.toplam || 0);
    } catch {
      this.nzMessage.error('Personel listesi yüklenirken hata oluştu');
    }
    this.yukleniyor.set(false);
  }

  async departmanlariYukle() {
    try {
      const dept = await this.firmaService.departmanListesi(this.firmaId);
      this.departmanlar.set(dept || []);
    } catch {}
  }

  async istatistikleriYukle() {
    if (!this.firmaId) return;
    try {
      const tumListe = await this.firmaService.personelListesi(this.firmaId, 1, 9999, { aktif: true });
      const kartliSayisi = (tumListe.liste || []).filter(p => !!p.kartUid).length;
      this.istatistikler.set({
        toplam: tumListe.toplam || 0,
        aktif: tumListe.toplam || 0,
        kartli: kartliSayisi,
      });
    } catch {}
  }

  aramaMetniDegisti(metin: string) {
    this.aramaMetni = metin;
    if (this.aramaTimer) clearTimeout(this.aramaTimer);
    this.aramaTimer = setTimeout(() => {
      this.mevcutSayfa = 1;
      this.yukle();
    }, 400);
  }

  sayfaDegisti(sayfa: number) { this.mevcutSayfa = sayfa; this.yukle(); }
  limitDegisti(limit: number) { this.sayfaLimiti = limit; this.mevcutSayfa = 1; this.yukle(); }

  // ─── FORM ─────────────────────────────────────────

  yeniPersonelAc() {
    this.formDuzenlemeModu = false;
    this.formPersonelId = '';
    this.form = this.bosForm();
    this.formGorunur = true;
  }

  formAc(p: IFirmaPersonel) {
    this.formDuzenlemeModu = true;
    this.formPersonelId = p._id;
    this.form = {
      ad: p.ad || '', soyad: p.soyad || '', sicilNo: p.sicilNo || '',
      tcKimlik: p.tcKimlik || '', departmanId: p.departmanId || '',
      unvan: p.unvan || '', gorev: p.gorev || '',
      telefon: p.telefon || '', eposta: p.eposta || '',
    };
    this.formGorunur = true;
  }

  async formKaydet() {
    if (!this.form.ad?.trim() || !this.form.soyad?.trim()) {
      this.nzMessage.warning('Ad ve Soyad alanları zorunludur');
      return;
    }

    this.formKaydediliyor.set(true);
    try {
      const veri: Partial<IFirmaPersonel> = {
        ad: this.form.ad.trim(), soyad: this.form.soyad.trim(),
        sicilNo: this.form.sicilNo?.trim() || '', tcKimlik: this.form.tcKimlik?.trim() || '',
        departmanId: this.form.departmanId || '', unvan: this.form.unvan?.trim() || '',
        gorev: this.form.gorev?.trim() || '', telefon: this.form.telefon?.trim() || '',
        eposta: this.form.eposta?.trim() || '',
      };

      if (this.formDuzenlemeModu) {
        await this.firmaService.personelGuncelle(this.formPersonelId, this.firmaId, veri);
        this.nzMessage.success('Personel güncellendi');
      } else {
        await this.firmaService.personelEkle(this.firmaId, veri);
        this.nzMessage.success('Personel eklendi');
      }

      this.formGorunur = false;
      this.yukle();
      this.istatistikleriYukle();
    } catch {
      this.nzMessage.error('İşlem sırasında hata oluştu');
    }
    this.formKaydediliyor.set(false);
  }

  async personelSil(p: IFirmaPersonel) {
    try {
      await this.firmaService.personelSil(p._id, this.firmaId);
      this.nzMessage.success(`${p.ad} ${p.soyad} silindi`);
      this.yukle();
      this.istatistikleriYukle();
    } catch {
      this.nzMessage.error('Silme işleminde hata oluştu');
    }
  }

  // ─── KART ─────────────────────────────────────────

  kartModalAc(p: IFirmaPersonel) {
    this.kartHedefPersonel = p;
    this.kartUidGirdi = '';
    this.kartModalGorunur = true;
    this.kartDinleniyor.set(true);
  }

  async kartAta() {
    const uid = this.kartUidGirdi?.trim();
    if (!uid) { this.nzMessage.warning('Kart UID giriniz'); return; }
    if (!this.kartHedefPersonel) return;

    this.kartKaydediliyor.set(true);
    try {
      const mukerrer = await this.firmaService.kartUidKontrol(this.firmaId, uid, this.kartHedefPersonel._id);
      if (mukerrer) {
        this.nzMessage.error('Bu kart UID başka bir personele atanmış');
        this.kartKaydediliyor.set(false);
        return;
      }

      await this.firmaService.kartAtama(this.kartHedefPersonel._id, this.firmaId, uid);
      this.nzMessage.success(`Kart başarıyla atandı: ${this.kartHedefPersonel.ad} ${this.kartHedefPersonel.soyad}`);
      this.kartModalGorunur = false;
      this.kartHedefPersonel = null;
      this.kartDinleniyor.set(false);
      this.yukle();
      this.istatistikleriYukle();
    } catch {
      this.nzMessage.error('Kart atama sırasında hata oluştu');
    }
    this.kartKaydediliyor.set(false);
  }

  async kartIptalEt(p: IFirmaPersonel) {
    try {
      await this.firmaService.kartIptal(p._id, this.firmaId);
      this.nzMessage.success(`${p.ad} ${p.soyad} kart iptal edildi`);
      this.yukle();
      this.istatistikleriYukle();
    } catch {
      this.nzMessage.error('Kart iptal sırasında hata oluştu');
    }
  }

  // ─── DETAY ────────────────────────────────────────

  detayAc(p: IFirmaPersonel) {
    this.detayPersonel = p;
    this.detayGorunur = true;
  }

  // ─── YARDIMCI ─────────────────────────────────────

  avatarHarf(p: IFirmaPersonel): string {
    return ((p.ad || '')[0] || '').toUpperCase() + ((p.soyad || '')[0] || '').toUpperCase();
  }

  avatarRenk(p: IFirmaPersonel): string {
    if (!p?._id) return this.renkler[0];
    let h = 0;
    for (let i = 0; i < p._id.length; i++) h = p._id.charCodeAt(i) + ((h << 5) - h);
    return this.renkler[Math.abs(h) % this.renkler.length];
  }

  kartKisa(uid: string): string {
    if (!uid || uid.length <= 8) return uid || '';
    return uid.substring(0, 4) + '..' + uid.substring(uid.length - 4);
  }

  private bosForm() {
    return {
      ad: '', soyad: '', sicilNo: '', tcKimlik: '',
      departmanId: '', unvan: '', gorev: '',
      telefon: '', eposta: '',
    };
  }

  private uidCikar(veri: string): string {
    const t = veri.trim();
    if (!t) return '';
    if (t.startsWith('{')) {
      try {
        const j = JSON.parse(t);
        const v = j.uid || j.UID || j.cardId || j.card_id || j.id || j.tag || '';
        if (v) return String(v).replace(/[\s:.\-,]/g, '').toUpperCase();
      } catch {}
    }
    const m = t.match(/(?:UID|Tag|ID|Card)\s*[:=]\s*(.+)/i);
    if (m) {
      const h = m[1].replace(/0x/gi, '').replace(/[\s:.\-,]/g, '').toUpperCase();
      if (h.length >= 4) return h;
    }
    const hm = t.match(/\b[0-9A-Fa-f]{4,20}\b/);
    if (hm) return hm[0].toUpperCase();
    return '';
  }
}
